import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useKdsTickets } from "@/hooks/useKdsTickets";
import { useKdsActions } from "@/hooks/useKdsActions";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useProductionLocations } from "@/hooks/useProductionLocations";
import { supabase } from "@/integrations/supabase/client";
import { KdsTopBar } from "@/components/kds/KdsTopBar";
import { KdsTicketCard } from "@/components/kds/KdsTicketCard";
import { KdsTotalizer } from "@/components/kds/KdsTotalizer";
import type { KdsOrderFilter } from "@/components/kds/KdsFilterBar";
import { Loader2 } from "lucide-react";

export default function KdsStationScreen() {
  const { locationId } = useParams<{ locationId: string }>();
  const { activeRestaurant } = useRestaurantContext();
  const { locations } = useProductionLocations();
  const { tickets, isLoading } = useKdsTickets(locationId || null);
  const actions = useKdsActions(activeRestaurant?.id || null);

  const [slaWarning, setSlaWarning] = useState(600);
  const [slaCritical, setSlaCritical] = useState(1200);

  useEffect(() => {
    if (!locationId) return;
    supabase
      .from("kds_station_config")
      .select("sla_warning_seconds, sla_critical_seconds")
      .eq("production_location_id", locationId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setSlaWarning(data.sla_warning_seconds);
          setSlaCritical(data.sla_critical_seconds);
        }
      });
  }, [locationId]);

  const [filter, setFilter] = useState<KdsOrderFilter>(() => {
    return (localStorage.getItem("kds-filter") as KdsOrderFilter) || "all";
  });

  useEffect(() => {
    localStorage.setItem("kds-filter", filter);
  }, [filter]);

  const location = locations.find(l => l.id === locationId);
  const stationName = location?.name || "Estação KDS";

  const filteredTickets = filter === "all"
    ? tickets
    : tickets.filter(t => t.orderType === filter);

  const pendingTickets = filteredTickets.filter(t => t.status !== 'done');
  const doneTickets = filteredTickets.filter(t => t.status === 'done');

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <KdsTopBar
        stationName={stationName}
        activeFilter={filter}
        onFilterChange={setFilter}
        ticketCount={pendingTickets.length}
      />

      <div className="flex-1 flex overflow-hidden">
        {/* Main grid - pending tickets only */}
        <div className="flex-1 overflow-y-auto p-4">
          {pendingTickets.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-zinc-500 text-lg">Nenhum pedido na fila</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {pendingTickets.map((ticket) => (
                <KdsTicketCard
                  key={ticket.orderId}
                  ticket={ticket}
                  slaWarning={slaWarning}
                  slaCritical={slaCritical}
                  onStartItem={actions.startItem}
                  onDoneItem={actions.doneItem}
                  onRecallItem={actions.recallItem}
                  onDoneTicket={actions.doneTicket}
                  onRecallTicket={actions.recallTicket}
                />
              ))}
            </div>
          )}
        </div>

        {/* Done tickets sidebar */}
        {doneTickets.length > 0 && (
          <div className="w-72 shrink-0 bg-zinc-800/60 border-l border-zinc-700 overflow-y-auto">
            <div className="px-3 py-3 border-b border-zinc-700">
              <h2 className="text-sm font-semibold text-emerald-400 uppercase tracking-wide">
                ✓ Liberados ({doneTickets.length})
              </h2>
            </div>
            <div className="p-2 space-y-2">
              {doneTickets.map((ticket) => (
                <KdsTicketCard
                  key={ticket.orderId}
                  ticket={ticket}
                  slaWarning={slaWarning}
                  slaCritical={slaCritical}
                  onStartItem={actions.startItem}
                  onDoneItem={actions.doneItem}
                  onRecallItem={actions.recallItem}
                  onDoneTicket={actions.doneTicket}
                  onRecallTicket={actions.recallTicket}
                />
              ))}
            </div>
          </div>
        )}

        {/* Totalizer sidebar */}
        <KdsTotalizer tickets={pendingTickets} />
      </div>
    </div>
  );
}
